import { Avatar, Box, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleApiError, tzDayjs } from '~/utils/helper';
import styles from './Profile.module.scss';
import DefaultImage from '~/assets/images/default.png';
import { ArrowLeft2Icon, Vector2Icon, TimestampIcon } from '~/assets/icons';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useNavigate } from 'react-router-dom';
import actionLogApi from '~/apis/action-log';
import { IActionLog } from '~/utils/interface/actionLog';
import { commonActions } from '~/thunks/common/commonSlice';

const cx = classNames.bind(styles);

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/profile');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const { isLoading } = useAppSelector((state) => state.common);

  const [actionLogs, setActionLogs] = useState<Array<IActionLog>>([]);

  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  const pageSize: number = 6;
  const pageIndex: number = 1;

  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  useEffect(() => {
    if (!currentUser?.username) return;

    getActionLogs(currentUser?.username);
  }, [currentUser?.username]);

  const getActionLogs = async (username?: string) => {
    try {
      const response = await actionLogApi.getActionLogs({
        username: username,
        pageIndex: 1,
        pageSize,
        getall: false,
      });

      const data = response.data.result.data;
      setActionLogs(data);
      setPage(pageIndex);

      if (data?.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const fetchMoreData = async () => {
    if (isLoading || !hasMore || !currentUser?.username) return;

    dispatch(commonActions.setIsLoading(true));

    const nextPage = page + 1;

    try {
      const response = await actionLogApi.getActionLogs({
        username: currentUser.username,
        pageIndex: nextPage,
        pageSize,
        getall: false,
      });

      const data = response.data.result.data ?? [];

      if (!data.length) {
        setHasMore(false);
        return;
      }

      setActionLogs((prev) => [...prev, ...data]);
      setPage(nextPage);

      if (data.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  const handleScroll = (e: any) => {
    if (isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollTop + clientHeight >= scrollHeight - 20) {
      fetchMoreData();
    }
  };

  return (
    <Box className={cx('profile')}>
      <Box className='flex bg-[#123B6D] p-5 w-full mb-3 items-center'>
        <ArrowLeft2Icon className='w-4 ml-5 mr-2 hover:cursor-pointer' onClick={() => navigate('/admin/dashboard')} />
        <Typography className='text-white text-xl font-semibold'>{t('title')}</Typography>
      </Box>
      <Box className='flex justify-center'>
        <Box className='flex justify-between bg-white rounded-xl w-[666px]'>
          <Box className='w-full'>
            <Box className='flex items-start border border-[#D7DEE7] rounded-xl w-[634px] m-3'>
              <Avatar className='w-10 h-10 m-4' alt={'Avatar'} src={currentUser.image ?? DefaultImage} />
              <Box className='my-3'>
                <Typography className='text-[#161A1F] text-lg font-semibold mb-1'>{currentUser.username}</Typography>
                <Typography className='text-[#161A1F] text-base font-normal mb-1'>{currentUser.role_name}</Typography>
                <Typography className='text-[#161A1F] text-base font-normal mb-1'>{currentUser.name}</Typography>
                <Typography className='text-[#161A1F] text-base font-normal mb-1'>{currentUser.email}</Typography>
                <Typography className='text-[#161A1F] text-base font-normal mb-1'>{currentUser.phone}</Typography>
                <Typography className='text-[#161A1F] text-base font-normal'>{currentUser.address}</Typography>
              </Box>
            </Box>
            {actionLogs?.length > 0 && (
              <Box className='flex flex-col border border-[#D7DEE7] rounded-xl w-[634px] mt-3 mx-3'>
                <Box className='flex justify-between p-2 w-full'>
                  <Typography className='text-[#161A1F] text-lg font-semibold'>{t('action_logs')}</Typography>
                  <Box
                    className='flex hover:cursor-pointer hover:opacity-70'
                    onClick={() => navigate('/admin/action-logs')}
                  >
                    <Vector2Icon className='mr-2' />
                    <Typography className='text-[#2E89C8] text-base font-semibold'>
                      {t('viewAll', { ns: 'admin/common' })}
                    </Typography>
                  </Box>
                </Box>
                <Box className='mb-3 overflow-auto max-h-[601px]' onScroll={handleScroll}>
                  <Box className='w-full p-3'>
                    <Box className='relative border-l border-[#D7DEE7]'>
                      {actionLogs?.map((item: IActionLog, index) => (
                        <Box key={index} className='mb-6 ml-6 relative'>
                          <Typography
                            component={'span'}
                            className='absolute -left-[31px] flex items-center justify-center w-3 h-3 bg-white border border-[#2E89C8] rounded-full'
                          ></Typography>
                          <Box>
                            <Typography className='font-semibold text-[#404854] text-base relative -top-1.5'>
                              {item.obj_type_namme}
                            </Typography>
                            <Typography className='text-[#404854] text-base font-normal'>{item.description}</Typography>
                            <Box className='flex items-center text-[#404854] text-base font-normal mt-2'>
                              <TimestampIcon className='w-4 h-4 mr-2' />
                              {item.action_time ? tzDayjs(item.action_time).format('HH:mm DD/MM/YYYY') : ''}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(Profile);
